import type {
	AutoCombineEntryV2,
	EntryMatter,
	EntryMiniTransaction,
	EntryStatus,
} from "@/shared/cache";
import type { ApiResponse, RequestOptions } from "@/shared/types/request";
import { unitsFromSeconds } from "@/shared/utils/units";
import type { NewEntryGroupFields } from "@/v2/lib/entryGroupSelection";
import {
	createAction,
	EntryMutationOutcomeError,
	type QueuedAction,
} from "./actionTypes";

/**
 * Dependencies for entry-group mutations. The combine table is a frontend
 * projection of backend `entry_group` rows plus child/source `entry` rows.
 */
export type AutoCombineActionDeps = {
	makeApiRequest: (opts: RequestOptions) => Promise<ApiResponse>;
	updateAutoCombine: (
		updater: (prev: AutoCombineEntryV2[]) => AutoCombineEntryV2[],
	) => () => void;
	refreshAutoCombine: () => Promise<void>;
	refreshEntryGroups: () => Promise<void>;
	getAutoCombine: () => AutoCombineEntryV2[];
	reconcileAffectedEntryGroups?: (data: unknown) => boolean;
};

async function apiCall(
	deps: AutoCombineActionDeps,
	opts: RequestOptions,
): Promise<ApiResponse> {
	const resp = await deps.makeApiRequest(opts);
	if (resp.status === "ERROR") throw new Error(resp.error || "Request failed");
	deps.reconcileAffectedEntryGroups?.(resp.data);
	return resp;
}

function refreshCombineAndEntries(deps: AutoCombineActionDeps): Promise<void> {
	// Auto-combine rows and entry groups share the same React Query cache key.
	// Invalidating both aliases causes duplicate `/v2/entry-groups` refetches.
	return deps.refreshAutoCombine();
}

const AFFECTED_ENTRY_GROUPS_QUERY = { includeAffectedEntryGroups: true };

type AutoCombineToast = QueuedAction["toast"];
type AutoCombineUpdater = (prev: AutoCombineEntryV2[]) => AutoCombineEntryV2[];

function stripUndefined<T extends object>(obj: T): Partial<T> {
	return Object.fromEntries(
		Object.entries(obj).filter(([, value]) => value !== undefined),
	) as Partial<T>;
}

function composeAutoCombineUpdaters(
	...updaters: AutoCombineUpdater[]
): AutoCombineUpdater {
	return (prev) => updaters.reduce((next, updater) => updater(next), prev);
}

type CreateAutoCombineActionArgs = {
	deps: AutoCombineActionDeps;
	optimistic?: AutoCombineUpdater | null;
	execute: () => Promise<unknown>;
	toast: AutoCombineToast;
	onSuccess?: () => void | Promise<void>;
	onError?: () => void | Promise<void>;
	refreshOnSuccess?: boolean;
	refreshOnError?: boolean;
};

async function refreshThen(
	deps: AutoCombineActionDeps,
	callback?: () => void | Promise<void>,
): Promise<void> {
	try {
		await refreshCombineAndEntries(deps);
		await callback?.();
	} catch {
		// Preserve existing fire-and-forget error handling while still allowing
		// callers to wait until the refresh attempt has settled.
	}
}

/**
 * Creates the single queued mutation shape used by the auto-combine table.
 * Optimistic-safe actions expose an optimistic updater that the React Query
 * mutation applies in onMutate and rolls back on error. Successful mutation
 * responses opt into `includeAffectedEntryGroups=true` and reconcile those
 * authoritative rows directly into every `entry_groups` cache variant.
 *
 * Post-response-only actions (export, add/duplicate, narrative regeneration)
 * do not pre-mark rows. They patch returned affected groups when the server
 * responds and only fall back to a broad invalidate when a caller explicitly
 * opts into refreshOnSuccess/refreshOnError.
 */
export function createAutoCombineAction({
	deps,
	optimistic = null,
	execute,
	toast,
	onSuccess,
	onError,
	refreshOnSuccess = false,
	refreshOnError = false,
}: CreateAutoCombineActionArgs): QueuedAction {
	return createAction({
		execute,
		optimistic: optimistic ? () => deps.updateAutoCombine(optimistic) : null,
		rollback: null,
		toast,
		onSuccess: () =>
			refreshOnSuccess ? refreshThen(deps, onSuccess) : onSuccess?.(),
		onError: () => (refreshOnError ? refreshThen(deps, onError) : onError?.()),
	});
}

function compareMiniTransactions(
	a: EntryMiniTransaction,
	b: EntryMiniTransaction,
): number {
	const aKey = `${a.date || "9999-99-99"}T${a.time ?? "99:99"}`;
	const bKey = `${b.date || "9999-99-99"}T${b.time ?? "99:99"}`;
	const timeOrder = aKey.localeCompare(bKey);
	return timeOrder || a.id.localeCompare(b.id);
}

function markNarrativeSourceDirty(
	entry: AutoCombineEntryV2,
	nowIso: string,
): AutoCombineEntryV2 {
	if (entry.status === "exported" || entry.status === "deleted") return entry;
	if (entry.miniTransactions.length === 0) return entry;
	return {
		...entry,
		narrativeSourceDirtyAt: entry.narrativeSourceDirtyAt ?? nowIso,
	};
}

function markMiniNewSinceNarrativeGeneration(
	entry: AutoCombineEntryV2,
	mini: EntryMiniTransaction,
	nowIso: string,
): EntryMiniTransaction {
	if (!entry.narrativeGeneratedAt) return mini;
	return {
		...mini,
		modifiedAt: mini.modifiedAt ?? nowIso,
		isNewSinceNarrativeGeneration: true,
	};
}

function recomputeParentDisplayFields(
	entry: AutoCombineEntryV2,
): AutoCombineEntryV2 {
	const miniTransactions = [...entry.miniTransactions].sort(
		compareMiniTransactions,
	);
	const first = miniTransactions[0];
	const last = miniTransactions[miniTransactions.length - 1];
	// Keep backend-owned duration totals intact; affected-group reconciliation
	// patches the authoritative rollup after mutations complete.
	return {
		...entry,
		miniTransactions,
		date: first?.date ?? "",
		startTime: first?.time ?? null,
		endTime: last?.time ?? null,
	};
}

function sameMiniTransactionList(
	a: EntryMiniTransaction[],
	b: EntryMiniTransaction[],
): boolean {
	return a.length === b.length && a.every((mini, index) => mini === b[index]);
}

export function recomputeAffectedAutoCombineParentRows(
	entryIds: string[],
): AutoCombineUpdater {
	const ids = new Set(entryIds);
	return (prev) =>
		prev.map((entry) =>
			ids.has(entry.id) ? recomputeParentDisplayFields(entry) : entry,
		);
}

export function patchAutoCombineParentStatus(
	entryIds: string[],
	status: EntryStatus,
): AutoCombineUpdater {
	const ids = new Set(entryIds);
	return (prev) =>
		prev.map((entry) => (ids.has(entry.id) ? { ...entry, status } : entry));
}

export function patchAutoCombineParentEntryGroup(
	entryId: string,
	patch: {
		matter?: EntryMatter | null;
		task?: string | null;
		activity?: string | null;
		units?: number;
		unitsSeconds?: number | null;
		isDurationDirty?: boolean;
		description?: string;
		status?: EntryStatus;
	},
): AutoCombineUpdater {
	return (prev) =>
		prev.map((entry) => {
			if (entry.id !== entryId) return entry;
			return {
				...entry,
				...(patch.matter !== undefined ? { matter: patch.matter } : {}),
				...(patch.description !== undefined
					? { description: patch.description }
					: {}),
				...(patch.units !== undefined ? { units: patch.units } : {}),
				...(patch.unitsSeconds !== undefined
					? { unitsSeconds: patch.unitsSeconds }
					: {}),
				...(patch.isDurationDirty !== undefined
					? { isDurationDirty: patch.isDurationDirty }
					: {}),
				...(patch.status !== undefined ? { status: patch.status } : {}),
				codes:
					patch.task !== undefined || patch.activity !== undefined
						? {
								task:
									patch.task !== undefined
										? patch.task
										: (entry.codes?.task ?? null),
								activity:
									patch.activity !== undefined
										? patch.activity
										: (entry.codes?.activity ?? null),
							}
						: entry.codes,
			};
		});
}

export type EntryGroupNarrativeResponse = {
	id?: string;
	narrative?: string | null;
	predictedNarrative?: string | null;
	predicted_narrative?: string | null;
	isNarrativeDirty?: boolean | null;
	is_narrative_dirty?: boolean | null;
	narrativeSourceEntryIds?: string[] | null;
	narrative_source_entry_ids?: string[] | null;
	narrativeSourceDirtyAt?: string | null;
	narrative_source_dirty_at?: string | null;
	narrativeGeneratedAt?: string | null;
	narrative_generated_at?: string | null;
	narrativeEditedAt?: string | null;
	narrative_edited_at?: string | null;
};

function hasOwn<T extends object>(obj: T, key: keyof T): boolean {
	return Object.keys(obj).includes(String(key));
}

export function firstEntryGroupResponse(
	data: unknown,
	entryId: string,
): EntryGroupNarrativeResponse | null {
	if (!data || typeof data !== "object") return null;
	const maybeList = data as { data?: unknown };
	if (Array.isArray(maybeList.data)) {
		return (
			maybeList.data.find((group): group is EntryGroupNarrativeResponse =>
				Boolean(
					group &&
						typeof group === "object" &&
						(group as EntryGroupNarrativeResponse).id === entryId,
				),
			) ?? null
		);
	}
	if (Array.isArray(data)) {
		return (
			data.find((group): group is EntryGroupNarrativeResponse =>
				Boolean(
					group &&
						typeof group === "object" &&
						(group as EntryGroupNarrativeResponse).id === entryId,
				),
			) ?? null
		);
	}
	const group = data as EntryGroupNarrativeResponse;
	return group.id === entryId ? group : null;
}

function firstEntryGroupIdResponse(data: unknown): string | null {
	const isEntryGroup = (
		value: unknown,
	): value is EntryGroupNarrativeResponse & { id: string } => {
		if (!value || typeof value !== "object") return false;
		const id = (value as EntryGroupNarrativeResponse).id;
		return typeof id === "string" && id.startsWith("eg_");
	};
	const maybeList = data as { data?: unknown } | undefined;
	if (Array.isArray(maybeList?.data)) {
		return maybeList.data.find(isEntryGroup)?.id ?? null;
	}
	if (Array.isArray(data)) {
		return data.find(isEntryGroup)?.id ?? null;
	}
	return isEntryGroup(data) ? data.id : null;
}

function nullableStringField(
	group: EntryGroupNarrativeResponse,
	camelKey: keyof EntryGroupNarrativeResponse,
	snakeKey: keyof EntryGroupNarrativeResponse,
): string | null | undefined {
	if (hasOwn(group, camelKey)) return group[camelKey] as string | null;
	if (hasOwn(group, snakeKey)) return group[snakeKey] as string | null;
	return undefined;
}

export function patchAutoCombineParentNarrativeFromResponse(
	entryId: string,
	group: EntryGroupNarrativeResponse,
): AutoCombineUpdater {
	return (prev) =>
		prev.map((entry) => {
			if (entry.id !== entryId) return entry;
			const narrativeSourceEntryIds = hasOwn(group, "narrativeSourceEntryIds")
				? group.narrativeSourceEntryIds
				: group.narrative_source_entry_ids;
			const isNarrativeDirty = hasOwn(group, "isNarrativeDirty")
				? group.isNarrativeDirty
				: group.is_narrative_dirty;
			const narrativeSourceDirtyAt = nullableStringField(
				group,
				"narrativeSourceDirtyAt",
				"narrative_source_dirty_at",
			);
			const narrativeGeneratedAt = nullableStringField(
				group,
				"narrativeGeneratedAt",
				"narrative_generated_at",
			);
			const narrativeEditedAt = nullableStringField(
				group,
				"narrativeEditedAt",
				"narrative_edited_at",
			);

			const narrativeIsCurrent = narrativeSourceDirtyAt === null;

			return {
				...entry,
				...(hasOwn(group, "narrative")
					? { description: group.narrative ?? "" }
					: {}),
				...(isNarrativeDirty !== undefined
					? { isNarrativeDirty: isNarrativeDirty ?? false }
					: {}),
				...(narrativeSourceEntryIds !== undefined
					? { narrativeSourceEntryIds: narrativeSourceEntryIds ?? [] }
					: {}),
				...(narrativeSourceDirtyAt !== undefined
					? { narrativeSourceDirtyAt }
					: {}),
				...(narrativeGeneratedAt !== undefined ? { narrativeGeneratedAt } : {}),
				...(narrativeEditedAt !== undefined ? { narrativeEditedAt } : {}),
				...(narrativeIsCurrent
					? {
							miniTransactions: entry.miniTransactions.map((mini) => ({
								...mini,
								isNewSinceNarrativeGeneration: false,
							})),
						}
					: {}),
			};
		});
}

export function patchAutoCombineSourceEntry(
	miniId: string,
	patch: { summary?: string; time?: string; unitsSeconds?: number | null },
): AutoCombineUpdater {
	const nowIso = new Date().toISOString();
	return (prev) =>
		prev.map((entry) => {
			let changed = false;
			const miniTransactions = entry.miniTransactions.map((mini) => {
				if (mini.id !== miniId) return mini;
				changed = true;
				const unitsSeconds =
					patch.unitsSeconds !== undefined
						? patch.unitsSeconds
						: mini.unitsSeconds;
				return {
					...mini,
					...(patch.summary !== undefined ? { summary: patch.summary } : {}),
					...(patch.time !== undefined ? { time: patch.time } : {}),
					...(patch.unitsSeconds !== undefined
						? { unitsSeconds, units: unitsFromSeconds(unitsSeconds ?? 0) }
						: {}),
					modifiedAt: nowIso,
					isNewSinceNarrativeGeneration: Boolean(entry.narrativeGeneratedAt),
				};
			});
			if (!changed) return entry;
			return markNarrativeSourceDirty(
				recomputeParentDisplayFields({ ...entry, miniTransactions }),
				nowIso,
			);
		});
}

export function removeAutoCombineSourceEntries(
	miniIds: string[],
): AutoCombineUpdater {
	const ids = new Set(miniIds);
	const nowIso = new Date().toISOString();
	return (prev) =>
		prev.map((entry) => {
			const miniTransactions = entry.miniTransactions.filter(
				(mini) => !ids.has(mini.id),
			);
			if (miniTransactions.length === entry.miniTransactions.length)
				return entry;
			return markNarrativeSourceDirty(
				recomputeParentDisplayFields({ ...entry, miniTransactions }),
				nowIso,
			);
		});
}

export function moveAutoCombineSourceEntriesToParent(
	miniIds: string[],
	targetEntryId: string,
): AutoCombineUpdater {
	const ids = new Set(miniIds);
	const nowIso = new Date().toISOString();
	return (prev) => {
		const moved = new Map<string, EntryMiniTransaction>();
		for (const entry of prev) {
			for (const mini of entry.miniTransactions) {
				if (ids.has(mini.id)) moved.set(mini.id, mini);
			}
		}
		const movedInSelectionOrder = miniIds
			.map((id) => moved.get(id))
			.filter((mini): mini is EntryMiniTransaction => Boolean(mini));

		return prev.map((entry) => {
			const withoutMoved = entry.miniTransactions.filter(
				(mini) => !ids.has(mini.id),
			);
			const miniTransactions =
				entry.id === targetEntryId
					? [
							...withoutMoved,
							...movedInSelectionOrder
								.filter(
									(mini) =>
										!withoutMoved.some((existing) => existing.id === mini.id),
								)
								.map((mini) =>
									markMiniNewSinceNarrativeGeneration(entry, mini, nowIso),
								),
						]
					: withoutMoved;
			if (sameMiniTransactionList(miniTransactions, entry.miniTransactions)) {
				return entry;
			}
			return markNarrativeSourceDirty(
				recomputeParentDisplayFields({ ...entry, miniTransactions }),
				nowIso,
			);
		});
	};
}

async function fetchActiveMatterGroupId(
	matter: EntryMatter,
	deps: AutoCombineActionDeps,
): Promise<string | null> {
	if (!matter.id) return null;

	const resp = await deps.makeApiRequest({
		endpoint: "/v2/entry-groups",
		method: "GET",
		queryParams: {
			limit: 1,
			where: JSON.stringify({
				matterId: matter.id,
				status: { $in: ["tracked", "reviewed"] },
			}),
		},
	});
	if (resp.status === "ERROR")
		throw new Error(resp.error || "Failed to fetch target entry group");

	const groups =
		(
			resp.data as
				| { data?: Array<{ id?: string; status?: string }> }
				| undefined
		)?.data ?? [];
	const target = groups.find(
		(group) =>
			group.id && (group.status === "tracked" || group.status === "reviewed"),
	);
	return target?.id ?? null;
}

// ── Action factories ─────────────────────────────────────────────────────

/**
 * PATCH /v2/entry-groups/{targetEgId}/entries — move selected source entries
 * into a target entry group.
 */
export function moveMiniTransactionsAction(
	miniIds: string[],
	targetEntryId: string,
	deps: AutoCombineActionDeps,
): QueuedAction {
	return createAutoCombineAction({
		deps,
		optimistic: moveAutoCombineSourceEntriesToParent(miniIds, targetEntryId),
		execute: async () => {
			return apiCall(deps, {
				endpoint: `/v2/entry-groups/${targetEntryId}/entries`,
				method: "PATCH",
				queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
				body: { addEntryIds: miniIds },
			});
		},
		toast: {
			error: "Failed to move source entries",
		},
	});
}

/**
 * Move selected source entries into an active group for the chosen matter, or
 * create a new group for that matter when none exists.
 */
export function bulkEditMiniTransactionsMatterAction(
	miniIds: string[],
	matter: EntryMatter,
	deps: AutoCombineActionDeps,
): QueuedAction {
	const target = deps
		.getAutoCombine()
		.find(
			(entry) =>
				entry.matter?.id === matter.id &&
				(entry.status === "tracked" || entry.status === "reviewed"),
		);
	return createAutoCombineAction({
		deps,
		optimistic: target
			? moveAutoCombineSourceEntriesToParent(miniIds, target.id)
			: null,
		execute: async () => {
			const targetGroupId = await fetchActiveMatterGroupId(matter, deps);
			if (targetGroupId) {
				return apiCall(deps, {
					endpoint: `/v2/entry-groups/${targetGroupId}/entries`,
					method: "PATCH",
					queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
					body: { addEntryIds: miniIds },
				});
			}
			return apiCall(deps, {
				endpoint: "/v2/entry-groups",
				method: "POST",
				queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
				body: {
					data: [
						{
							status: "tracked",
							type: "group",
							matterId: matter.id,
							clientId: matter.client,
							entryIds: miniIds,
						},
					],
				},
			});
		},
		toast: {
			error: "Failed to move source entries",
		},
	});
}

/**
 * POST /v2/entry-groups with entryIds — create a new target group and move the
 * selected source entries into it. The backend refreshes emptied source groups
 * and returns authoritative affected rows when requested.
 */
export function createEntryGroupFromSourceEntriesAction(
	miniIds: string[],
	fields: NewEntryGroupFields,
	deps: AutoCombineActionDeps,
): QueuedAction {
	return createAutoCombineAction({
		deps,
		execute: async () => {
			return apiCall(deps, {
				endpoint: "/v2/entry-groups",
				method: "POST",
				queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
				body: {
					data: [
						{
							status: "tracked",
							type: "group",
							entryIds: miniIds,
							...stripUndefined(fields),
						},
					],
				},
			});
		},
		toast: {
			loading: `Moving ${miniIds.length} ${
				miniIds.length === 1 ? "entry" : "entries"
			} to a new group…`,
			success: `Moved ${miniIds.length} ${
				miniIds.length === 1 ? "entry" : "entries"
			} to a new group`,
			error: "Failed to move selection to a new group",
		},
	});
}

type ParentStatusTarget = "tracked" | "reviewed" | "deleted";

function parentStatusAction(
	entryIds: string[],
	target: ParentStatusTarget,
	error: string,
	deps: AutoCombineActionDeps,
): QueuedAction {
	return createAutoCombineAction({
		deps,
		optimistic: patchAutoCombineParentStatus(entryIds, target),
		execute: async () => {
			return apiCall(deps, {
				endpoint: "/v2/entry-groups",
				method: "PATCH",
				queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
				body: { data: entryIds.map((id) => ({ id, status: target })) },
			});
		},
		toast: { error },
	});
}

export function markAutoCombineStatusAction(
	entryIds: string[],
	target: "tracked" | "reviewed",
	deps: AutoCombineActionDeps,
): QueuedAction {
	return parentStatusAction(entryIds, target, "Failed to update status", deps);
}

export function exportAutoCombineAction(
	entryIds: string[],
	deps: AutoCombineActionDeps,
): QueuedAction {
	return createAutoCombineAction({
		deps,
		execute: async () => {
			const resp = await deps.makeApiRequest({
				endpoint: "/v2/entry-groups/export",
				method: "POST",
				queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
				body: { entryGroupIds: entryIds },
			});
			if (resp.status === "ERROR")
				throw new Error(resp.error || "Failed to export entries");
			deps.reconcileAffectedEntryGroups?.(resp.data);

			const outcomes =
				(
					resp.data as
						| {
								data?: Array<{
									status?: string;
									error?: { message?: string } | null;
								}>;
						  }
						| undefined
				)?.data ?? [];
			const failed = outcomes.filter((outcome) => outcome.status === "failed");
			const exportedEntryGroupIds = outcomes
				.filter((outcome) => outcome.status === "exported")
				.map((outcome) => (outcome as { entryGroupId?: string }).entryGroupId)
				.filter((id): id is string => Boolean(id));
			if (failed.length > 0) {
				const errors = failed.map(
					(outcome) => outcome.error?.message ?? "Failed to export entry",
				);
				throw new EntryMutationOutcomeError(
					failed.length === 1
						? errors[0]
						: `${failed.length} entries failed: ${[...new Set(errors)].join("; ")}`,
					{ exportedEntryGroupIds },
				);
			}
			return {
				exportedEntryGroupIds: exportedEntryGroupIds.length
					? exportedEntryGroupIds
					: entryIds,
			};
		},
		toast: {
			loading: `Exporting ${entryIds.length} ${entryIds.length === 1 ? "entry" : "entries"}…`,
			success: `Exported ${entryIds.length} ${entryIds.length === 1 ? "entry" : "entries"}`,
			error: (err) =>
				err instanceof Error ? err.message : "Failed to export entries",
		},
	});
}

export function deleteAutoCombineAction(
	entryIds: string[],
	deps: AutoCombineActionDeps,
): QueuedAction {
	return parentStatusAction(
		entryIds,
		"deleted",
		"Failed to delete entries",
		deps,
	);
}

export function deleteMiniTransactionsAction(
	miniIds: string[],
	deps: AutoCombineActionDeps,
): QueuedAction {
	return createAutoCombineAction({
		deps,
		optimistic: removeAutoCombineSourceEntries(miniIds),
		execute: async () => {
			return apiCall(deps, {
				endpoint: "/v2/entries",
				method: "PATCH",
				queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
				body: { data: miniIds.map((id) => ({ id, isDeleted: true })) },
			});
		},
		toast: {
			error: "Failed to delete source entries",
		},
	});
}

export function deleteAutoCombineSelectionAction(
	entryIds: string[],
	miniIds: string[],
	deps: AutoCombineActionDeps,
): QueuedAction {
	const optimisticUpdaters: AutoCombineUpdater[] = [];
	if (entryIds.length > 0) {
		optimisticUpdaters.push(patchAutoCombineParentStatus(entryIds, "deleted"));
	}
	if (miniIds.length > 0) {
		optimisticUpdaters.push(removeAutoCombineSourceEntries(miniIds));
	}

	return createAutoCombineAction({
		deps,
		optimistic:
			optimisticUpdaters.length > 0
				? composeAutoCombineUpdaters(...optimisticUpdaters)
				: null,
		execute: async () => {
			return apiCall(deps, {
				endpoint: "/v2/entry-state/delete-selection",
				method: "POST",
				body: {
					entryGroupIds: entryIds,
					entryIds: miniIds,
				},
			});
		},
		toast: {
			error: "Failed to delete selection",
		},
		refreshOnError: true,
	});
}

export function restoreAutoCombineAction(
	entryIds: string[],
	deps: AutoCombineActionDeps,
): QueuedAction {
	return parentStatusAction(
		entryIds,
		"tracked",
		"Failed to restore entries",
		deps,
	);
}

export type AddAutoCombineEntryGroupPayload = {
	idempotencyKey: string;
	matterId?: string;
	clientId?: string | null;
	taskCodeId?: string;
	activityCodeId?: string;
	narrative: string;
	startTime: string;
	endTime: string;
	durationSeconds: number;
	shouldExport?: boolean;
};

export function addAutoCombineEntryGroupAction(
	payload: AddAutoCombineEntryGroupPayload,
	deps: AutoCombineActionDeps,
	callbacks?: { onSuccess?: () => void; onError?: () => void },
): QueuedAction {
	return createAutoCombineAction({
		deps,
		execute: async () => {
			const resp = await apiCall(deps, {
				endpoint: "/v2/entry-groups",
				method: "POST",
				headers: { "Idempotency-Key": payload.idempotencyKey },
				queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
				body: {
					data: [
						{
							status: "tracked",
							type: "group",
							matterId: payload.matterId,
							clientId: payload.clientId ?? undefined,
							taskCodeId: payload.taskCodeId,
							activityCodeId: payload.activityCodeId,
							narrative: payload.narrative,
							entries: [
								{
									startTime: payload.startTime,
									endTime: payload.endTime,
									durationSeconds: payload.durationSeconds,
									matterId: payload.matterId,
								},
							],
						},
					],
				},
			});
			if (!payload.shouldExport) return resp;

			const entryGroupId = firstEntryGroupIdResponse(resp.data);
			if (!entryGroupId) throw new Error("Entry group id missing");
			const exportResp = await deps.makeApiRequest({
				endpoint: "/v2/entry-groups/export",
				method: "POST",
				queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
				body: { entryGroupIds: [entryGroupId] },
			});
			if (exportResp.status === "ERROR")
				throw new EntryMutationOutcomeError(
					exportResp.error || "Failed to export entry",
					{ committedResult: resp },
				);
			deps.reconcileAffectedEntryGroups?.(exportResp.data);

			const failed =
				(
					exportResp.data as
						| {
								data?: Array<{
									status?: string;
									error?: { message?: string } | null;
								}>;
						  }
						| undefined
				)?.data?.find((outcome) => outcome.status === "failed") ?? null;
			if (failed)
				throw new EntryMutationOutcomeError(
					failed.error?.message ?? "Failed to export entry",
					{ committedResult: resp },
				);
			return { exportedEntryGroupIds: [entryGroupId] };
		},
		toast: {
			error: payload.shouldExport
				? "Failed to add and export entry"
				: "Failed to add entry",
		},
		onSuccess: callbacks?.onSuccess,
		onError: callbacks?.onError,
	});
}

export type EditAutoCombineEntryGroupPayload = {
	matterId?: string | null;
	matter?: EntryMatter | null;
	taskCodeId?: string | null;
	activityCodeId?: string | null;
	taskCodeDisplay?: string | null;
	activityCodeDisplay?: string | null;
	durationSeconds?: number;
	clearDurationOverride?: true;
	narrative?: string;
	status?: EntryStatus;
};

export function editAutoCombineEntryGroupAction(
	entryId: string,
	payload: EditAutoCombineEntryGroupPayload,
	deps: AutoCombineActionDeps,
): QueuedAction {
	const narrativeOnly =
		payload.narrative !== undefined &&
		payload.matterId === undefined &&
		payload.taskCodeId === undefined &&
		payload.activityCodeId === undefined &&
		payload.durationSeconds === undefined &&
		payload.clearDurationOverride === undefined &&
		payload.status === undefined;
	return createAutoCombineAction({
		deps,
		optimistic: patchAutoCombineParentEntryGroup(entryId, {
			matter: payload.matter,
			task: payload.taskCodeDisplay ?? payload.taskCodeId,
			activity: payload.activityCodeDisplay ?? payload.activityCodeId,
			...(payload.durationSeconds !== undefined
				? {
						units: Number((payload.durationSeconds / 3600).toFixed(2)),
						unitsSeconds: payload.durationSeconds,
						isDurationDirty: true,
					}
				: {}),
			...(payload.clearDurationOverride ? { isDurationDirty: false } : {}),
			...(payload.narrative !== undefined
				? { description: payload.narrative }
				: {}),
			status: payload.status,
		}),
		execute: async () => {
			const resp = await deps.makeApiRequest({
				endpoint: "/v2/entry-groups",
				method: "PATCH",
				queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
				body: {
					data: [
						{
							id: entryId,
							...(payload.matterId !== undefined
								? { matterId: payload.matterId }
								: {}),
							...(payload.taskCodeId !== undefined
								? { taskCodeId: payload.taskCodeId }
								: {}),
							...(payload.activityCodeId !== undefined
								? { activityCodeId: payload.activityCodeId }
								: {}),
							...(payload.durationSeconds !== undefined
								? { durationSeconds: payload.durationSeconds }
								: {}),
							...(payload.clearDurationOverride
								? { clearDurationOverride: true }
								: {}),
							...(payload.narrative !== undefined
								? { narrative: payload.narrative }
								: {}),
							...(payload.status ? { status: payload.status } : {}),
						},
					],
				},
			});
			if (resp.status === "ERROR")
				throw new Error(resp.error || "Request failed");
			deps.reconcileAffectedEntryGroups?.(resp.data);
			if (narrativeOnly) {
				const updated = firstEntryGroupResponse(resp.data, entryId);
				if (updated) {
					deps.updateAutoCombine(
						patchAutoCombineParentNarrativeFromResponse(entryId, updated),
					);
				}
			}
			return resp;
		},
		toast: {
			error: "Failed to update entry",
		},
		refreshOnSuccess: !narrativeOnly,
		refreshOnError: !narrativeOnly,
	});
}

export function regenerateAutoCombineNarrativeAction(
	entryId: string,
	deps: AutoCombineActionDeps,
): QueuedAction {
	return createAutoCombineAction({
		deps,
		execute: async () => {
			return apiCall(deps, {
				endpoint: `/v2/entry-groups/${entryId}/regenerate-narrative`,
				method: "POST",
				body: { mode: "accept" },
			});
		},
		toast: {
			loading: "Regenerating narrative…",
			success: "Narrative regenerated",
			error: "Failed to regenerate narrative",
		},
	});
}

export function editAutoCombineSourceEntryAction(
	entryId: string,
	// `unitsSeconds` is omitted when the attorney did not touch the units. The
	// server treats any duration it receives as a deliberate override, and for a
	// timer fill that overwrites the measured time, so a description-only save
	// must not send one.
	payload: { unitsSeconds?: number; time: string; description: string },
	deps: AutoCombineActionDeps,
): QueuedAction {
	const mini = deps
		.getAutoCombine()
		.flatMap((entry) => entry.miniTransactions)
		.find((candidate) => candidate.id === entryId);
	const startTime =
		mini?.date && payload.time
			? `${mini.date}T${payload.time}:00.000Z`
			: undefined;

	return createAutoCombineAction({
		deps,
		optimistic: patchAutoCombineSourceEntry(entryId, {
			summary: payload.description,
			time: payload.time,
			...(payload.unitsSeconds !== undefined
				? { unitsSeconds: payload.unitsSeconds }
				: {}),
		}),
		execute: async () => {
			return apiCall(deps, {
				endpoint: `/v2/entries/${entryId}`,
				method: "PATCH",
				queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
				body: {
					...(startTime ? { startTime } : {}),
					...(payload.unitsSeconds !== undefined
						? { durationSeconds: payload.unitsSeconds }
						: {}),
					summary: payload.description,
				},
			});
		},
		toast: {
			error: "Failed to update source entry",
		},
	});
}

export function duplicateAutoCombineEntryGroupAction(
	entryId: string,
	deps: AutoCombineActionDeps,
): QueuedAction {
	return createAutoCombineAction({
		deps,
		execute: async () => {
			return apiCall(deps, {
				endpoint: "/v2/entry-groups",
				method: "POST",
				queryParams: AFFECTED_ENTRY_GROUPS_QUERY,
				body: { data: [{ duplicate: { entryGroupIds: [entryId] } }] },
			});
		},
		toast: {
			error: "Failed to duplicate entry",
		},
	});
}
